
import { Component, Vue, Watch } from "vue-property-decorator";
import DimssaButton, { ButtonState } from "@/components/shared/dimssa-button.vue";
import * as Models from "@gigalot/data-models";
import { mapFields } from "@/store";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import lodash from "lodash";
import { changeTrackingComponentFragment } from "@/helpers/gql";
import { listCustomFeeder } from "@/helpers/graphql-list-items";
import { getItemFromServer } from "@/helpers/get-item-from-server";
import wNumb from "wnumb";
import { currencyFormat, massFormat } from "@/helpers";

@Component({
  components: {
    DimssaButton,
  },
  computed: {
    ...mapFields({
      reference: "reprocessBatch.reference",
      settled: "reprocessBatch.settled",
      type: "reprocessBatch.type",
      description: "reprocessBatch.description",
      quantityExpected: "reprocessBatch.quantityExpected",
    }),
  },
  mixins: [validationMixin],
  validations: {
    reference: { required },
  },
})
export default class ReprocessBatch extends Vue {
  tab = null;
  changed: boolean = false;
  refreshButtonState: ButtonState = "ready";
  manualCostTotal: string = "";
  manualCostTotalVatIncl: string = "";
  manualVatTotal: string = "";
  vat: number = 0.15;
  animals: any[] = [];
  animalsHeaders: any[] = [
    {
      text: "#",
      value: "numInQueue",
    },
    {
      text: "Tag ID",
      value: "visualTag",
    },
    {
      text: "Breed",
      value: "breed",
    },
    {
      text: "Gender",
      value: "gender",
    },
    {
      text: "Destination",
      value: "kraalType",
    },
    {
      text: "Kraal",
      value: "kraalId",
    },
    {
      text: "Sick",
      value: "isSick",
    },
    {
      text: "Vaccination",
      value: "vaccination",
    },
    {
      text: "Time",
      value: "time",
    },
    {
      text: "Mass",
      value: "mass",
    },
  ];

  moment = this.$store.state.moment;
  currencyFormat = currencyFormat;
  massFormat = massFormat;

  currencyPerMassFormat(amt: any) {
    return wNumb({
      mark: ".",
      thousand: " ",
      prefix: "R ",
      suffix: "/kg",
      decimals: 2,
    }).to(parseFloat(amt));
  }

  get batchCosting(): Models.Costing[] {
    return ((this.reprocessBatch as any).costing as Models.Costing[] | undefined) ?? [];
  }

  set batchCosting(costing: Models.Costing[]) {
    this.$store.commit("updateUserLocationField", { path: "reprocessBatch.costing", value: costing });
  }

  save() {
    if (!this.reprocessBatch.reference) {
      this.$store.commit("popup/displayOk", [`Can not save, Reference required.`]);
      return;
    }

    const cfGuid = this.reprocessBatch.customFeeder?.guid;
    const ncfGuid = this.reprocessBatch.newCustomFeeder?.guid;
    if (cfGuid && ncfGuid && cfGuid === ncfGuid) {
      this.$store.commit("popup/displayOk", [`Can not save, Owner and New Owner must be different.`]);
      return;
    }

    this.$store.commit("popup/displayYesNo", {
      messages: ["Are you sure that you want to save?"],
      yesAction: async () => {
        try {
          this.$store.commit("popup/displayWait", ["Saving..."]);

          console.log(this.$store.getters["storage"]().reprocessBatch);
          let reprocessBatchClone: Models.ReprocessBatch = lodash.cloneDeep(this.$store.getters["storage"]().reprocessBatch);

          reprocessBatchClone.metadata = this.$store.getters["user/getUpstreamMetadata"]();
          let json = await this.$store.dispatch(
            "graphQl",
            {
              gql: `mutation setReprocessBatch($guid: String!, $reprocessBatch: ReprocessBatchInput!) {
                setReprocessBatch(guid: $guid, reprocessBatch: $reprocessBatch) { 
                  ...changeTrackingFields
                }
              }${changeTrackingComponentFragment}`,
              variables: { guid: this.$store.state.user.location.guid, reprocessBatch: reprocessBatchClone },
              timeout: 10 * 1000,
            },
            { root: true }
          );
          console.log("graphQL: " + JSON.stringify(json));
          let reprocessBatch = this.$store.getters["storage"]().reprocessBatch as Models.ReprocessBatch;
          reprocessBatch.changeTracking = json.data.reprocessBatch;

          this.customFeederAlreadySelected = !!reprocessBatch.customFeeder;
          this.newCustomFeederAlreadySelected = !!reprocessBatch.newCustomFeeder;

          this.changed = false;
          this.$store.commit("popup/hide");
        } catch (err) {
          console.log(err);
          this.$store.commit("popup/hide");
          this.$store.commit("popup/displayOk", { messages: [`Error: ${err}`] });
        }
      },
    });
  }

  back() {
    this.$store.commit("popup/displayYesNo", {
      messages: ["Are you sure? Unsaved work will be lost."],
      yesAction: () => {
        this.$store.commit("updateUserLocationField", { path: "reprocessBatch", value: {} });
        this.$router.push({ path: "list/reprocess-batches" });
      },
    });
  }

  @Watch("changed")
  onChangeChanged(val: any) {
    if (this.changed) this.$store.commit("navFuncs", { save: this.save, back: this.back });
    else
      this.$store.commit("navFuncs", {
        save: undefined,
        back: () => {
          this.$store.commit("updateUserLocationField", { path: "reprocessBatch", value: {} });
          this.$router.push({ path: "list/reprocess-batches" });
        },
      });
  }

  get reprocessBatch(): Models.ReprocessBatch {
    const ret: Models.ReprocessBatch = this.$store.getters["storage"]().reprocessBatch;
    return ret;
  }

  async refresh() {
    this.refreshButtonState = "busy";
    console.log("refresh " + this.reprocessBatch.guid);
    if (!this.reprocessBatch.guid) {
      this.refreshButtonState = "error";
      return;
    }
    try {
      let json = await this.$store.dispatch(
        "graphQl",
        {
          gql: Models.gql.queries.processingResults,
          variables: { guid: this.$store.state.user.location.guid, batchGuid: this.reprocessBatch.guid },
        },
        { root: true }
      );
      if (!json.data || !json.data.processingResults) throw Error("No processing results returned from query");
      let processingResults: Models.ProcessingResult[] = json.data.processingResults;
      let animals: any[] = [];
      for (let processingResult of processingResults) {
        let i = 0;

        json = await this.$store.dispatch(
          "graphQl",
          {
            gql: Models.gql.queries.processedAnimals,
            variables: { guid: this.$store.state.user.location.guid, processingResultGuid: processingResult.guid },
          },
          { root: true }
        );

        let processedAnimals: Models.ProcessedAnimal[] = [...json.data.processedAnimals, ...processingResult.processedAnimals];

        for (let animal of processedAnimals) {
          animals.unshift({
            numInQueue: ++i,
            guid: animal.guid,
            processingResultGuid: animal.processingResultGuid,
            sgtin: animal.sgtin,
            visualTag: animal.sgtin ? (animal.sgtin[36] + animal.sgtin.slice(-5)).replace(/^0+/, "") : "",
            time: this.moment(animal.time).format("YYYY-MM-DD HH:mm:ss"),
            mass: animal.mass,
            gender: animal.gender,
            breed: animal.breed,
            kraalType: animal.sorting && animal.sorting.destination ? animal.sorting.destination.type : "N/A",
            kraalId: animal.sorting && animal.sorting.destination ? animal.sorting.destination.kraalId : "N/A",
            isSick: animal.vaccinations.find((dav) => dav.description === "sick") ? "Y" : "",
            vaccination:
              animal.vaccinations && animal.vaccinations.length && animal.vaccinations[0].vaccination
                ? animal.vaccinations[0].vaccination.vaccination.description
                : "N/A",
          });
        }
      }
      this.animals = animals;
      this.calculatePurchasePrice();
      this.refreshButtonState = "success";
    } catch (err) {
      this.refreshButtonState = "error";
      console.log(err);
      throw err;
    }
  }

  customFeederAlreadySelected: boolean = false;
  newCustomFeederAlreadySelected: boolean = false;

  async created() {
    this.$store.commit("navFuncs", {
      save: undefined,
      back: () => {
        this.$store.commit("updateUserLocationField", { path: "reprocessBatch", value: {} });
        this.$router.push({ path: "list/reprocess-batches" });
      },
    });

    if (this.customFeeder) {
      const cf = this.customFeeder;
      this.selectedCustomFeederListItem = { guid: cf.guid, text: `${cf.name}${cf.companies[0]?.name ? ` - ${cf.companies[0]?.name}` : ``}` };
      this.getSelectedCustomFeederButtonState = "success";
      this.customFeederAlreadySelected = true;
    }
    if (this.newCustomFeeder) {
      const cf = this.newCustomFeeder;
      this.selectedNewCustomFeederListItem = { guid: cf.guid, text: `${cf.name}${cf.companies[0]?.name ? ` - ${cf.companies[0]?.name}` : ``}` };
      this.getSelectedNewCustomFeederButtonState = "success";
      this.newCustomFeederAlreadySelected = true;
    }
    this.manualCostTotalChange((this.reprocessBatch as any).manualCostTotal);
    this.calculatePurchasePriceType();
    this.calculatePurchasePrice();
    this.getCustomFeederListItems();
  }

  async mounted() {
    this.animals = [];
    await this.refresh();
  }

  customFeederListItems: { guid: string; text: string }[] = [];
  selectedCustomFeederListItem: { guid: string; text: string } | "" = "";
  selectedNewCustomFeederListItem: { guid: string; text: string } | "" = "";
  getCustomFeederListItemsButtonState: ButtonState = "ready";
  getSelectedCustomFeederButtonState: ButtonState = "ready";
  getSelectedNewCustomFeederButtonState: ButtonState = "ready";

  async getCustomFeederListItems() {
    try {
      this.getCustomFeederListItemsButtonState = "busy";
      this.customFeederListItems = [];
      let items = await listCustomFeeder(true);
      this.customFeederListItems = items.map((i: any) => ({ guid: i.guid, text: `${i.name}${i.companyName ? ` - ${i.companyName}` : ``}` }));
      this.customFeederListItems = this.customFeederListItems.sort((p1, p2) => (p1.text > p2.text ? 1 : -1));
      this.getCustomFeederListItemsButtonState = "success";
    } catch (err) {
      console.error(err);
      this.getCustomFeederListItemsButtonState = "error";
    }

    //If selected is not found in list then add it
    if (this.customFeeder && !this.customFeederListItems.find((c) => c.guid === (this.customFeeder as Models.CustomFeeder).guid)) {
      const cf = this.customFeeder as Models.CustomFeeder;
      this.customFeederListItems.push({ guid: cf.guid, text: cf.name });
    }
    if (this.newCustomFeeder && !this.customFeederListItems.find((c) => c.guid === (this.newCustomFeeder as Models.CustomFeeder).guid)) {
      const cf = this.newCustomFeeder as Models.CustomFeeder;
      this.customFeederListItems.push({ guid: cf.guid, text: cf.name });
    }
  }

  ////////
  // customFeeder

  async getCustomFeederFromServer() {
    if (!this.selectedCustomFeederListItem || !this.selectedCustomFeederListItem.guid) {
      this.customFeeder = "";
      this.getSelectedCustomFeederButtonState = "ready";
      return;
    }
    try {
      this.getSelectedCustomFeederButtonState = "busy";
      const customFeeder = await getItemFromServer("CustomFeeder", this.selectedCustomFeederListItem.guid);
      this.customFeeder = customFeeder;
      this.getSelectedCustomFeederButtonState = "success";
    } catch (err) {
      this.getSelectedCustomFeederButtonState = "error";
      console.error(err);
      this.customFeeder = "";
    }
  }

  get modelSelectedCustomFeederListItem(): { guid: string; text: string } | "" {
    return this.selectedCustomFeederListItem;
  }

  set modelSelectedCustomFeederListItem(selectedCustomFeederListItem: { guid: string; text: string } | "") {
    if (!selectedCustomFeederListItem) selectedCustomFeederListItem = "";

    if (
      this.selectedCustomFeederListItem &&
      selectedCustomFeederListItem &&
      selectedCustomFeederListItem.guid &&
      this.selectedCustomFeederListItem.guid === selectedCustomFeederListItem.guid
    )
      return; // if same already selected then do nothing

    this.selectedCustomFeederListItem = selectedCustomFeederListItem;
    this.getCustomFeederFromServer();
  }

  get customFeeder(): Models.CustomFeeder | "" {
    return this.reprocessBatch?.customFeeder ? this.reprocessBatch.customFeeder : "";
  }

  set customFeeder(customFeeder: Models.CustomFeeder | "") {
    this.$store.commit("updateUserLocationField", { path: "reprocessBatch.customFeeder", value: customFeeder === "" ? undefined : customFeeder });
  }

  // customFeeder
  ////////
  // newCustomFeeder

  async getNewCustomFeederFromServer() {
    if (!this.selectedNewCustomFeederListItem || !this.selectedNewCustomFeederListItem.guid) {
      this.newCustomFeeder = "";
      this.getSelectedNewCustomFeederButtonState = "ready";
      return;
    }
    try {
      this.getSelectedNewCustomFeederButtonState = "busy";
      const newCustomFeeder = await getItemFromServer("CustomFeeder", this.selectedNewCustomFeederListItem.guid);
      this.newCustomFeeder = newCustomFeeder;
      this.getSelectedNewCustomFeederButtonState = "success";
    } catch (err) {
      this.getSelectedNewCustomFeederButtonState = "error";
      console.error(err);
      this.newCustomFeeder = "";
    }
  }

  get modelSelectedNewCustomFeederListItem(): { guid: string; text: string } | "" {
    return this.selectedNewCustomFeederListItem;
  }

  set modelSelectedNewCustomFeederListItem(selectedNewCustomFeederListItem: { guid: string; text: string } | "") {
    if (!selectedNewCustomFeederListItem) selectedNewCustomFeederListItem = "";

    if (
      this.selectedNewCustomFeederListItem &&
      selectedNewCustomFeederListItem &&
      selectedNewCustomFeederListItem.guid &&
      this.selectedNewCustomFeederListItem.guid === selectedNewCustomFeederListItem.guid
    )
      return; // if same already selected then do nothing

    this.selectedNewCustomFeederListItem = selectedNewCustomFeederListItem;
    this.getNewCustomFeederFromServer();
  }

  get newCustomFeeder(): Models.CustomFeeder | "" {
    return this.reprocessBatch?.newCustomFeeder ? this.reprocessBatch.newCustomFeeder : "";
  }

  set newCustomFeeder(customFeeder: Models.CustomFeeder | "") {
    this.$store.commit("updateUserLocationField", { path: "reprocessBatch.newCustomFeeder", value: customFeeder === "" ? undefined : customFeeder });
  }

  // newCustomFeeder
  ////////

  manualCostTotalChange(manualCostTotal: any) {
    this.manualCostTotal = manualCostTotal;
    if (manualCostTotal === "" || manualCostTotal === null || isNaN(manualCostTotal)) {
      this.manualCostTotalVatIncl = "";
      this.manualVatTotal = "";
      this.$store.commit("updateUserLocationField", { path: "reprocessBatch.manualCostTotal", value: undefined });
    } else {
      let tempManualCostTotalExclVat = parseFloat(manualCostTotal);
      this.manualCostTotalVatIncl = (tempManualCostTotalExclVat * (1.0 + this.vat)).toFixed(2);
      this.manualVatTotal = (tempManualCostTotalExclVat * this.vat).toFixed(2);
      this.$store.commit("updateUserLocationField", { path: "reprocessBatch.manualCostTotal", value: tempManualCostTotalExclVat });
    }
  }

  manualCostTotalVatInclChange(manualCostTotalVatIncl: any) {
    this.manualCostTotalVatIncl = manualCostTotalVatIncl;
    if (isNaN(manualCostTotalVatIncl)) {
      this.manualCostTotal = "";
      this.manualVatTotal = "";
      this.$store.commit("updateUserLocationField", { path: "reprocessBatch.manualCostTotal", value: undefined });
    } else {
      let tempManualCostTotalInclVat = parseFloat(manualCostTotalVatIncl);
      this.manualCostTotal = (tempManualCostTotalInclVat / (1.0 + this.vat)).toFixed(2);
      this.manualVatTotal = ((tempManualCostTotalInclVat / (1.0 + this.vat)) * this.vat).toFixed(2);
      this.$store.commit("updateUserLocationField", { path: "reprocessBatch.manualCostTotal", value: parseFloat(this.manualCostTotal) });
    }
  }

  purchasePriceTypeItems: any[] = [
    { description: "A - Single Purchase Price", type: "single-price" },
    { description: "B - Male and Female Purchase Prices", type: "gender-price" },
    { description: "C - Purchase Price per Mass Group", type: "mass-price" },
  ];

  purchasePriceType: any = "";
  purchasePrice: number | "" = "";
  purchasePriceMale: number | "" = "";
  purchasePriceFemale: number | "" = "";
  averagePricePerKg: any = "N/A";

  minMass(index: number) {
    let min = this.batchCosting[2 * index]?.min;
    return min !== undefined ? min : "";
  }

  maxMass(index: number) {
    let max = this.batchCosting[2 * index]?.max;
    return max !== undefined ? max : "";
  }

  setMaxMass(index: number, value: string) {
    let costing = [...this.batchCosting];
    let isLastCosting = 2 * index + 1 == costing.length - 1;
    let max = isNaN(parseFloat(value)) || value === "" ? undefined : parseFloat(value);
    if (!isLastCosting && max !== undefined) {
      costing[2 * (index + 1)].min = max;
      costing[2 * (index + 1) + 1].min = max;
    }
    costing[2 * index].max = max;
    costing[2 * index + 1].max = max;
    this.batchCosting = costing;
    this.calculatePurchasePrice();
    this.$forceUpdate();
  }

  getPrice(index: number) {
    let cost = this.batchCosting.length ? this.batchCosting[index]?.cost : undefined;
    return cost !== undefined ? cost : "";
  }

  setPrice(index: number, value: string) {
    let costing = [...this.batchCosting];
    if (!costing[index]) return;
    costing[index].cost = isNaN(parseFloat(value)) || value === "" ? undefined : parseFloat(value);
    this.batchCosting = costing;
    this.calculatePurchasePrice();
  }

  getPriceDifference(index: number) {
    let maleCost = this.batchCosting[2 * index];
    let femaleCost = this.batchCosting[2 * index + 1];
    if (!maleCost || !femaleCost || [maleCost.cost, femaleCost.cost].includes(undefined)) return "";
    return ((maleCost.cost as number) - (femaleCost.cost as number)).toFixed(2);
  }

  get massGroupCostings() {
    if (this.batchCosting.length % 2 !== 0) throw Error("mass-price costings must be an even number (male female pairs)");

    let ret = [];
    for (let i = 0; i < this.batchCosting.length; i += 2) {
      ret.push({ male: this.batchCosting[i], female: this.batchCosting[i + 1] });
    }
    return ret;
  }

  get massGroupTotals() {
    let ret = [];

    for (let cost of this.massGroupCostings) {
      let maleTotal = this.animals.filter((a) => {
        if (!cost.male || cost.male.min === undefined || cost.male.max === undefined) return false;
        return cost.male.min < a.mass && a.mass <= cost.male.max && "male" === a.gender.toLowerCase();
      }).length;

      let femaleTotal = this.animals.filter((a) => {
        if (!cost.female || cost.female.min === undefined || cost.female.max === undefined) return false;
        return cost.female.min < a.mass && a.mass <= cost.female.max && "female" === a.gender.toLowerCase();
      }).length;

      ret.push({ male: maleTotal, female: femaleTotal, total: maleTotal + femaleTotal });
    }

    return ret;
  }

  addMassGenderCosting() {
    let latest = this.massGroupCostings[this.massGroupCostings.length - 1];
    let costing = [...this.batchCosting];
    costing.push({ type: ["male", "mass"], min: latest.male.max }, { type: ["female", "mass"], min: latest.female.max });
    this.batchCosting = costing;
    this.calculatePurchasePrice();
    this.$forceUpdate();
  }

  removeMassGenderCosting(index: number) {
    let costing = [...this.batchCosting];
    costing.splice(2 * index, 2);
    this.batchCosting = costing;
    this.calculatePurchasePrice();
    this.$forceUpdate();
  }

  purchasePriceTypeChange(purchasePriceType: any) {
    switch (purchasePriceType.type) {
      case "single-price": {
        if (this._calculatePurchasePriceType()?.type === "single-price") break;
        this.batchCosting = [{ type: ["single"] } as Models.Costing];
        break;
      }
      case "gender-price": {
        if (this._calculatePurchasePriceType()?.type === "gender-price") break;
        this.batchCosting = [{ type: ["male"] } as Models.Costing, { type: ["female"] } as Models.Costing];
        break;
      }
      case "mass-price": {
        if (this._calculatePurchasePriceType()?.type === "mass-price") break;
        this.batchCosting = [{ type: ["male", "mass"], min: 0 } as Models.Costing, { type: ["female", "mass"], min: 0 } as Models.Costing];
        break;
      }
    }

    this.purchasePriceType = purchasePriceType;
    this.calculatePurchasePrice();
  }

  calculatePurchasePrice() {
    if (!this.purchasePriceType) {
      this.purchasePrice = "";
      this.purchasePriceMale = "";
      this.purchasePriceFemale = "";
      this.averagePricePerKg = "N/A";
      return;
    }

    let costing = this.batchCosting;
    switch (this.purchasePriceType.type) {
      case "single-price": {
        let totalMass = this.animals.map((a) => a.mass).reduce((acc, cur) => acc + cur, 0);
        let totalMaleMass = this.animals
          .filter((a) => a.gender.toLowerCase() === "male")
          .map((a) => a.mass)
          .reduce((acc, cur) => acc + cur, 0);
        let totalFemaleMass = this.animals
          .filter((a) => a.gender.toLowerCase() === "female")
          .map((a) => a.mass)
          .reduce((acc, cur) => acc + cur, 0);
        if (!costing[0] || costing[0].cost === undefined) {
          this.purchasePrice = "";
          this.purchasePriceMale = "";
          this.purchasePriceFemale = "";
          this.averagePricePerKg = "N/A";
          return;
        }
        this.purchasePrice = costing[0].cost * totalMass;
        this.purchasePriceMale = costing[0].cost * totalMaleMass;
        this.purchasePriceFemale = costing[0].cost * totalFemaleMass;
        this.averagePricePerKg = costing[0].cost;
        break;
      }
      case "gender-price": {
        let totalMaleMass = this.animals
          .filter((a) => a.gender.toLowerCase() === "male")
          .map((a) => a.mass)
          .reduce((acc, cur) => acc + cur, 0);
        let maleCost = costing.find((c) => c.type.includes("male"));
        let totalFemaleMass = this.animals
          .filter((a) => a.gender.toLowerCase() === "female")
          .map((a) => a.mass)
          .reduce((acc, cur) => acc + cur, 0);
        let femaleCost = costing.find((c) => c.type.includes("female"));
        if (!maleCost || maleCost.cost === undefined || !femaleCost || femaleCost.cost === undefined) {
          this.purchasePrice = "";
          this.purchasePriceMale = "";
          this.purchasePriceFemale = "";
          this.averagePricePerKg = "N/A";
          return;
        }

        this.purchasePrice = maleCost.cost * totalMaleMass + femaleCost.cost * totalFemaleMass;
        this.purchasePriceMale = maleCost.cost * totalMaleMass;
        this.purchasePriceFemale = femaleCost.cost * totalFemaleMass;
        this.averagePricePerKg = (maleCost.cost + femaleCost.cost) / 2.0;
        break;
      }
      case "mass-price": {
        this.purchasePrice = "";
        this.purchasePriceMale = "";
        this.purchasePriceFemale = "";
        this.averagePricePerKg = "N/A";

        let pricedAnimals = this.animals.map((a) => {
          let cost = costing.find((c) => {
            if (c.min === undefined || c.max === undefined) return false;
            return c.min < a.mass && a.mass <= c.max && c.type.find((t) => t.toLowerCase() === a.gender.toLowerCase());
          });

          if (!cost?.cost) return { ...a, calcPricePerKg: "N/A", calcPrice: "N/A" };
          return { ...a, calcPricePerKg: cost.cost as number, calcPrice: (cost.cost as number) * a.mass };
        });

        if (pricedAnimals.find((a) => a.calcPricePerKg === "N/A" || a.calcPrice === "N/A")) {
          this.purchasePrice = "";
          this.averagePricePerKg = "N/A";
        } else {
          let totals = { count: 0, calcPricePerKgTotal: 0, calcPriceTotal: 0, calcMalePriceTotal: 0, calcFemalePriceTotal: 0 };
          pricedAnimals.forEach((a) => {
            totals.count++;
            totals.calcPricePerKgTotal += a.calcPricePerKg;
            totals.calcPriceTotal += a.calcPrice;
            if (a.gender.toLowerCase() === "male") totals.calcMalePriceTotal += a.calcPrice;
            else if (a.gender.toLowerCase() === "female") totals.calcFemalePriceTotal += a.calcPrice;
          });
          this.purchasePrice = totals.calcPriceTotal;
          this.purchasePriceMale = totals.calcMalePriceTotal;
          this.purchasePriceFemale = totals.calcFemalePriceTotal;
          this.averagePricePerKg = totals.count > 0 ? totals.calcPricePerKgTotal / totals.count : "N/A";
        }
        break;
      }
    }
  }

  calculatePurchasePriceType() {
    this.purchasePriceType = this._calculatePurchasePriceType();
  }

  _calculatePurchasePriceType() {
    if (!this.batchCosting.length) return "";
    let singlePrice = lodash.find(this.batchCosting, (c) => c.type.includes("single"));
    let massPrice = lodash.find(this.batchCosting, (c) => c.type.includes("mass"));
    let malePrice = lodash.find(this.batchCosting, (c) => c.type.includes("male"));
    if (singlePrice) return lodash.find(this.purchasePriceTypeItems, (p) => p.type === "single-price");
    if (massPrice) return lodash.find(this.purchasePriceTypeItems, (p) => p.type === "mass-price");
    if (malePrice) return lodash.find(this.purchasePriceTypeItems, (p) => p.type === "gender-price");
    return "";
  }
}
